import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/shared/PageHero";
import PageSEO from "@/components/shared/PageSEO";
import Breadcrumbs from "@/components/shared/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Send,
  Linkedin,
  Twitter,
  Instagram,
  Facebook,
  Loader2
} from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const contactInfo = [
  {
    icon: MapPin,
    title: "Our Location",
    content: "Jeddah, Kingdom of Saudi Arabia",
    subtitle: "Head Office",
  },
  {
    icon: Phone,
    title: "Phone",
    content: "+966 5 737 505 48",
    subtitle: "Mon-Fri 9AM-6PM",
  },
  {
    icon: Mail,
    title: "Email",
    content: "info@sdcgulf.com",
    subtitle: "We'll respond within 24 hours",
  },
  {
    icon: Clock,
    title: "Working Hours",
    content: "Sun - Thu: 9:00 AM - 6:00 PM",
    subtitle: "Saudi Arabia Time (AST)",
  },
];

const socialLinks = [
  { icon: Linkedin, href: "https://www.linkedin.com/company/sdc-sustainable-development-company/", label: "LinkedIn" },
  { icon: Twitter, href: "https://twitter.com/Sdcgulf", label: "Twitter" },
  { icon: Instagram, href: "https://instagram.com/Sdcgulf", label: "Instagram" },
  { icon: Facebook, href: "https://www.facebook.com/people/شركة-تطوير-الاستدامة-التجارية/61585370007117/", label: "Facebook" },
];

const ContactPage = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('submit-contact-form', {
        body: formData,
      });

      if (error) {
        throw new Error(error.message || 'Failed to send message');
      }

      toast({
        title: "Message Sent!",
        description: data?.message || "Thank you for contacting us. We'll get back to you within 24 hours.",
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Contact form error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="Contact SDC - Micro Mobility Saudi Arabia"
        description="Contact SDC تطوير الاستدامة for micro mobility solutions, fleet quotes & partnerships in Saudi Arabia. اتصل بنا للحصول على حلول التنقل الصغير في السعودية. Jeddah headquarters."
        keywords="contact SDC, اتصل بتطوير الاستدامة, micro mobility quote Saudi Arabia, B2B contact KSA, fleet solutions inquiry, عرض أسعار مركبات كهربائية, mobility partnership Saudi, contact electric vehicle supplier Jeddah"
      />
      <Navbar />
      
      <PageHero
        title="Contact Us"
        subtitle="Get in Touch"
        description="Have questions about our mobility solutions? We'd love to hear from you. Reach out and let's discuss how we can help transform your operations."
        variant="vibrant"
      />

      <Breadcrumbs />

      {/* Contact Info Cards */}
      <section className="py-12 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-card border border-border text-center hover:border-primary/50 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <info.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-bold text-foreground mb-1">{info.title}</h3>
                <p className="text-foreground">{info.content}</p>
                <p className="text-sm text-muted-foreground mt-1">{info.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Form */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Send Us a Message
              </h2>
              <p className="text-muted-foreground mb-8">
                Fill out the form below and our team will get back to you as soon as possible.
              </p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Full Name *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="bg-card border-border"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Email Address *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      required
                      className="bg-card border-border"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Phone Number
                    </label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+966 XX XXX XXXX"
                      className="bg-card border-border"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Company Name
                    </label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="bg-card border-border"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Subject *
                  </label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    required
                    className="bg-card border-border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project or inquiry..."
                    rows={6}
                    required
                    className="bg-card border-border resize-none"
                  />
                </div>
                <Button type="submit" variant="hero" size="xl" className="w-full md:w-auto" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Map / Location Info */}
            <div>
              <div className="rounded-2xl bg-card border border-border overflow-hidden h-full">
                {/* Placeholder for map */}
                <div className="h-64 lg:h-80 bg-secondary/50 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 text-primary mx-auto mb-4" />
                    <p className="text-foreground font-medium">Jeddah, Saudi Arabia</p>
                    <p className="text-sm text-muted-foreground">Our headquarters location</p>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-foreground mb-4">Connect With Us</h3>
                  <p className="text-muted-foreground mb-6">
                    Follow us on social media to stay updated with our latest news, projects, 
                    and insights on sustainable mobility.
                  </p>
                  <div className="flex gap-4">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.href}
                        className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center hover:bg-primary/10 hover:text-primary transition-colors duration-300"
                        aria-label={social.label}
                      >
                        <social.icon className="w-5 h-5" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section with Accordion */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Have questions? Check out our most commonly asked questions or reach out to us directly.
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-card border border-border rounded-2xl px-6">
                <AccordionTrigger className="text-foreground font-semibold hover:no-underline">
                  What types of vehicles do you offer?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  We offer a comprehensive range of electric vehicles including patrol carts, security vehicles, 
                  electric unicycles, autonomous delivery robots, and medical transport solutions. All vehicles 
                  are designed for government and corporate fleet operations.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="bg-card border border-border rounded-2xl px-6">
                <AccordionTrigger className="text-foreground font-semibold hover:no-underline">
                  Do you provide maintenance services?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Yes, all our solutions include comprehensive maintenance packages with 24/7 technical support. 
                  We offer preventive maintenance, emergency repairs, spare parts supply, and fleet management 
                  software to ensure maximum uptime for your operations.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="bg-card border border-border rounded-2xl px-6">
                <AccordionTrigger className="text-foreground font-semibold hover:no-underline">
                  What areas do you serve?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  We operate across the GCC region with our headquarters in Jeddah, Saudi Arabia. Our services 
                  extend throughout Saudi Arabia, UAE, and other Gulf countries. We also support international 
                  projects for major events and government initiatives.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="bg-card border border-border rounded-2xl px-6">
                <AccordionTrigger className="text-foreground font-semibold hover:no-underline">
                  How do I request a fleet quote?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  You can request a quote by filling out the contact form above or emailing us directly at 
                  info@sdcgulf.com. Our team will assess your requirements and provide a customized proposal 
                  within 48 hours, including vehicle specifications, leasing options, and support packages.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-5" className="bg-card border border-border rounded-2xl px-6">
                <AccordionTrigger className="text-foreground font-semibold hover:no-underline">
                  Do you offer training for operators?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Yes, we provide comprehensive operator training as part of our fleet deployment. This includes 
                  vehicle operation, safety protocols, basic troubleshooting, and maintenance awareness. Training 
                  can be conducted on-site at your facility.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-6" className="bg-card border border-border rounded-2xl px-6">
                <AccordionTrigger className="text-foreground font-semibold hover:no-underline">
                  Can I rent electric vehicles for my event through LEMO?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Yes! Our LEMO service offers electric vehicle fleet rentals for events, conferences, government 
                  functions, and more. We handle delivery, setup, operator training, and collection. Visit our{" "}
                  <a href="/lemo" className="text-primary hover:underline">LEMO Rentals page</a> to learn more 
                  and request a booking.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
